import api from "@/lib/api";

// Anubis voice with a graceful fallback:
//  1) ElevenLabs (rich, natural) via POST /oracle/speak
//  2) the browser's built-in speech synthesis — free & unlimited — whenever
//     ElevenLabs is unavailable (e.g. monthly quota exhausted, offline).
// This guarantees Anubis always has a voice.

let currentAudio = null;
let cooldownUntil = 0; // when ElevenLabs recently failed, skip it for a while

export function stopSpeaking() {
  try { currentAudio?.pause(); } catch {}
  currentAudio = null;
  try { window.speechSynthesis?.cancel(); } catch {}
}

// Warm up the browser voice list (Chrome loads voices async).
if (typeof window !== "undefined" && window.speechSynthesis) {
  try { window.speechSynthesis.getVoices(); } catch {}
}

function pickBrowserVoice() {
  const synth = window.speechSynthesis;
  const voices = (synth && synth.getVoices && synth.getVoices()) || [];
  if (!voices.length) return null;
  // Prefer a deeper English male voice to echo Anubis; fall back to any English voice.
  return (
    voices.find((v) => /^en/i.test(v.lang) && /(daniel|george|arthur|oliver|male|david|fred|guy|alex|aaron)/i.test(v.name)) ||
    voices.find((v) => /^en-GB/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0]
  );
}

function browserSpeak(text, speed) {
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    if (!synth) { resolve(false); return; }
    try { synth.cancel(); } catch {}
    const u = new SpeechSynthesisUtterance(text);
    u.rate = Math.max(0.6, Math.min(1.4, speed || 1));
    u.pitch = 0.85;
    const v = pickBrowserVoice();
    if (v) { u.voice = v; u.lang = v.lang; }
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(true); } };
    u.onend = finish;
    u.onerror = finish;
    synth.speak(u);
  });
}

// Speak a single piece of text. Resolves when playback finishes (or is canceled).
// isCanceled: () => boolean lets the caller abort mid-way.
// Returns "eleven" | "browser" | "canceled".
export async function speakText(text, { voiceId, speed = 1.0, isCanceled, piperVoice } = {}) {
  const t = (text || "").trim();
  if (!t) return "canceled";

  const tryEleven = Date.now() >= cooldownUntil;
  if (tryEleven) {
    try {
      const res = await api.post(
        "/oracle/speak",
        { text: t.slice(0, 2500), voice_id: voiceId, speed, piper_voice: piperVoice || localStorage.getItem("piper_voice") || "ryan" },
        { responseType: "blob" }
      );
      if (isCanceled && isCanceled()) return "canceled";
      const type = (res.data && res.data.type) || "";
      if (type.startsWith("audio") && res.data.size > 200) {
        const url = URL.createObjectURL(res.data);
        const a = new Audio(url);
        currentAudio = a;
        try {
          await new Promise((resolve, reject) => {
            a.onended = resolve;
            a.onerror = reject;
            a.play().catch(reject);
          });
          return "eleven";
        } finally {
          URL.revokeObjectURL(url);
          if (currentAudio === a) currentAudio = null;
        }
      }
      throw new Error("empty audio");
    } catch {
      // ElevenLabs unavailable (quota/network/autoplay). Cool off, then use the free browser voice.
      cooldownUntil = Date.now() + 10 * 60 * 1000;
    }
  }

  if (isCanceled && isCanceled()) return "canceled";
  await browserSpeak(t, speed);
  return "browser";
}
